import React, { useState } from 'react';
import {
    Box,
    Button,
    capitalize,
    Card,
    CardActionArea,
    CardContent,
    CardMedia,
    Chip,
    Grid,
    Typography,
} from '@mui/material';
import { OwnerProfile } from '../../../types/owner';
import { Link } from 'react-router';
import { ExperienceDiscount } from '../../../types/experience';
import { format } from 'date-fns';

const OwnerProfileDiscountsCard = ({
    discount,
}: {
    discount: ExperienceDiscount;
}) => {
    const firstDate = discount.dates[0]?.startDate;
    const month = firstDate
        ? format(new Date(firstDate), 'yyyy-MM')
        : undefined;

    return (
        <Grid key={discount.id} item xs={12} sm={6} md={4}>
            <Card>
                <CardActionArea
                    component={Link}
                    to={{
                        pathname: `/viewExperiences/${discount.module?.type.toLowerCase()}/${discount.module?.id}`,
                        search: month ? `?month=${month}` : undefined,
                    }}
                    sx={{
                        position: 'relative',
                        overflow: 'hidden',
                        aspectRatio: 1,
                    }}
                >
                    <CardMedia
                        component='img'
                        image={discount.module?.photo}
                        alt={`${discount.module?.name} experience`}
                        sx={{
                            position: 'absolute',
                            inset: 0,
                            height: '100%',
                            width: '100%',
                            objectFit: 'cover',
                        }}
                    />
                    <CardContent
                        sx={{
                            position: 'relative',
                            height: '100%',
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'flex-end',
                            color: 'common.white',
                            background:
                                'linear-gradient(to top, rgba(0,0,0,0.72) 0%, transparent 55%)',
                        }}
                    >
                        <Typography color='inherit'>
                            {capitalize(discount.name)}{' '}
                            <Chip
                                size='small'
                                color='secondary'
                                label={`${discount.percentage}% off`}
                                sx={{ mr: 'auto' }}
                            />
                        </Typography>
                        {discount.dates.map((date, index) => (
                            <li key={index} style={{ listStyle: 'none' }}>
                                <Typography color='inherit' variant='body2'>
                                    From{' '}
                                    {new Date(
                                        date.startDate,
                                    ).toLocaleDateString('en-AU')}{' '}
                                    to{' '}
                                    {new Date(date.endDate).toLocaleDateString(
                                        'en-AU',
                                    )}
                                </Typography>
                            </li>
                        ))}
                    </CardContent>
                </CardActionArea>
            </Card>
        </Grid>
    );
};

const OwnerProfileDiscounts = ({ owner }: { owner: OwnerProfile }) => {
    const sortedDiscounts = [...owner.discounts].sort(
        (a, b) =>
            new Date(a.dates[0].startDate).getTime() -
            new Date(b.dates[0].startDate).getTime(),
    );

    const [viewAll, setViewAll] = useState(false);

    const displayedDiscounts =
        (viewAll ? sortedDiscounts : sortedDiscounts.slice(0, 3)) ?? [];

    return (
        <section aria-labelledby='owner-profile-discounts-heading'>
            <Typography
                id='owner-profile-discounts-heading'
                variant='h2'
                sx={{ pb: 2 }}
            >
                Discounts
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Grid container spacing={3}>
                    {displayedDiscounts.map((discount) => (
                        <OwnerProfileDiscountsCard
                            key={discount.id}
                            discount={discount}
                        />
                    ))}
                </Grid>
                {sortedDiscounts.length > 3 && (
                    <Button
                        variant='text'
                        color='primary'
                        onClick={() => setViewAll(!viewAll)}
                        sx={{ ml: 'auto' }}
                    >
                        Show {viewAll ? 'less' : 'more'} discounts
                    </Button>
                )}
            </Box>
        </section>
    );
};

export default OwnerProfileDiscounts;
